// The Berlin Food Map renderer (ADR 0007). Pins are Content API posts tagged #food-map with geo JSON in Code Injection.
(function () {
    const canvas = document.getElementById('food-map-canvas');
    if (!canvas) return;

    const cfg = window.HEIST_FOOD_MAP;
    if (!cfg) {
        console.warn(
            '[food-map] window.HEIST_FOOD_MAP missing; member auth or template wiring broken.',
        );
        return;
    }
    if (!cfg.mapboxToken || !cfg.contentApiKey) {
        console.warn(
            '[food-map] Mapbox token or Content API key not configured — Ghost Admin → Design → Customise.',
        );
        return;
    }
    if (typeof mapboxgl === 'undefined') {
        console.warn('[food-map] mapbox-gl library did not load; check CSP / network.');
        return;
    }

    const BERLIN_CENTER = [13.405, 52.52];
    const DEFAULT_ZOOM = 11;

    // Polychromie subset that reads on Dusk navy; excludes hexes reserved by other UI (docs/colour-palette.md).
    const CUISINE_PALETTE = [
        {bg: '#4e6498', on: '#fffceb'}, // bleu outremer 31 · 32020 (white)
        {bg: '#366c8e', on: '#fffceb'}, // bleu céruléen 31 · 32030 (white)
        {bg: '#7fb4b5', on: '#0e2d58'}, // céruléen moyen · 32032 (black)
        {bg: '#2c695a', on: '#fffceb'}, // vert anglais · 32040 (white)
        {bg: '#39774f', on: '#fffceb'}, // vert foncé · 32050 (white)
        {bg: '#87b26c', on: '#0e2d58'}, // vert 31 · 32051 (black)
        {bg: '#de6433', on: '#0e2d58'}, // orange · 32080 (black)
        {bg: '#e88e5a', on: '#0e2d58'}, // orange clair · 32081 (black)
        {bg: '#9c2128', on: '#fffceb'}, // rouge vermillon 31 · 32090 (white)
        {bg: '#811c35', on: '#fffceb'}, // rouge carmin · 32100 (white)
        {bg: '#e3b09b', on: '#0e2d58'}, // rose clair · 32102 (black)
        {bg: '#884333', on: '#fffceb'}, // l'ocre rouge · 32110 (white)
        {bg: '#d28b7a', on: '#0e2d58'}, // l'ocre rouge moyen · 32111 (black)
        {bg: '#a86a5d', on: '#0e2d58'}, // terre sienne brique · 32121 (black, marginal)
        {bg: '#e8c29e', on: '#0e2d58'}, // terre sienne claire 31 · 32122 (black)
        {bg: '#a33a29', on: '#fffceb'}, // rouge vermillon 59 · 4320A (white)
        {bg: '#de8da0', on: '#0e2d58'}, // rose vif · 4320C (black)
        {bg: '#a4aa35', on: '#0e2d58'}, // vert olive vif · 4320F (black)
        {bg: '#559778', on: '#0e2d58'}, // vert 59 · 4320G (black)
        {bg: '#f2b573', on: '#0e2d58'}, // ocre jaune clair · 4320L (black)
        {bg: '#6c2b3b', on: '#fffceb'}, // le rubis · 4320M (white)
        {bg: '#7facc6', on: '#0e2d58'}, // bleu céruléen 59 · 4320N (black)
        {bg: '#f7cc9d', on: '#0e2d58'}, // terre sienne claire 59 · 4320P (black)
        {bg: '#d15c32', on: '#0e2d58'}, // orange vif · 4320S (black, marginal)
        {bg: '#f4bd48', on: '#0e2d58'}, // le jaune vif · 4320W (black) — also used for the "New" indicator, but the star shape distinguishes
    ];

    // TEMP(testing): lock some cuisines to fixed colours; remove once hash-only vs editorial override is decided.
    const CUISINE_OVERRIDES = {
        'hash-cuisine-american': {bg: '#366c8e', on: '#fffceb'}, // bleu céruléen 31 · 32030 (distinct from Korean's vermillon and Pizza's olive)
        'hash-cuisine-german': {bg: '#de6433', on: '#0e2d58'}, // orange · 32080 (hash collides with Turkish at terre sienne claire; matches Döner's hashed colour)
        'hash-cuisine-pizza': {bg: '#a4aa35', on: '#0e2d58'}, // vert olive vif · 4320F
        'hash-cuisine-vegan': {bg: '#a1c977', on: '#0e2d58'}, // vert clair · 32052 — explicitly bright green for editorial signal
    };

    // Deterministic hash: the same slug always picks the same palette entry across reloads.
    function colorFromSlug(slug) {
        if (!slug) return null;
        if (CUISINE_OVERRIDES[slug]) return CUISINE_OVERRIDES[slug];
        const key = slug.replace(/^hash-cuisine-/, '');
        let hash = 0;
        for (let i = 0; i < key.length; i++) {
            hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
        }
        return CUISINE_PALETTE[hash % CUISINE_PALETTE.length];
    }

    // Cleared only after the config checks so the placeholder survives missing tokens; Mapbox wants an empty container.
    canvas.innerHTML = '';

    mapboxgl.accessToken = cfg.mapboxToken;
    const map = new mapboxgl.Map({
        container: 'food-map-canvas',
        style: cfg.mapboxStyle || 'mapbox://styles/mapbox/light-v11',
        center: BERLIN_CENTER,
        zoom: DEFAULT_ZOOM,
        attributionControl: true,
        // Mapbox Standard config doesn't ride the style URL; mirror any Studio changes here.
        config: {
            basemap: {
                theme: 'faded',
                lightPreset: 'night',
                show3dObjects: false,
                showPointOfInterestLabels: false,
                showAdminBoundaries: false,
                showPedestrianRoads: false,
            },
        },
    });
    map.addControl(new mapboxgl.NavigationControl({showCompass: false}), 'top-right');

    const pinRecords = [];
    const pinBySlug = new Map();
    const filterState = {
        cuisine: new Set(),
        area: new Set(),
        price: new Set(),
        isNew: false,
        searchQuery: '',
    };

    // ── URL state ────────────────────────────────────────────────────
    // replaceState (not pushState): no back-button entries per panel/chip change.
    const URL_FILTER_PREFIXES = {
        cuisine: 'hash-cuisine-',
        area: 'hash-area-',
        price: 'hash-price-',
    };

    function writeUrl(mutator) {
        const params = new URLSearchParams(window.location.search);
        mutator(params);
        const query = params.toString();
        const url = window.location.pathname + (query ? '?' + query : '') + window.location.hash;
        window.history.replaceState(null, '', url);
    }

    const urlState = {
        initial: new URLSearchParams(window.location.search),
        setPin: function (slug) {
            writeUrl(function (p) {
                if (slug) p.set('pin', slug);
                else p.delete('pin');
            });
        },
        setFilters: function (state) {
            writeUrl(function (p) {
                ['cuisine', 'area', 'price'].forEach(function (axis) {
                    const values = Array.from(state[axis]).map(function (slug) {
                        return slug.replace(URL_FILTER_PREFIXES[axis], '');
                    });
                    if (values.length) p.set(axis, values.join(','));
                    else p.delete(axis);
                });
                if (state.isNew) p.set('new', '1');
                else p.delete('new');
                if (state.searchQuery) p.set('q', state.searchQuery);
                else p.delete('q');
            });
        },
    };

    // Unknown URL slugs are dropped so they don't pollute filterState and re-emit on the next write.
    function hydrateFiltersFromUrl(params, pins) {
        const valid = {cuisine: new Set(), area: new Set(), price: new Set()};
        pins.forEach(function (p) {
            p.cuisines.forEach(function (c) {
                valid.cuisine.add(c.slug);
            });
            if (p.area) valid.area.add(p.area);
            if (p.price) valid.price.add(p.price);
        });
        const result = {
            cuisine: new Set(),
            area: new Set(),
            price: new Set(),
            isNew: false,
            searchQuery: '',
        };
        ['cuisine', 'area', 'price'].forEach(function (axis) {
            const raw = params.get(axis);
            if (!raw) return;
            raw.split(',').forEach(function (v) {
                const trimmed = v.trim();
                if (!trimmed) return;
                const fullSlug = URL_FILTER_PREFIXES[axis] + trimmed;
                if (valid[axis].has(fullSlug)) {
                    result[axis].add(fullSlug);
                } else {
                    console.info('[food-map] URL ' + axis + '=' + trimmed + ' unknown; ignoring.');
                }
            });
        });
        if (params.get('new') === '1') result.isNew = true;
        const q = params.get('q');
        if (q) result.searchQuery = q.toLowerCase().trim();
        return result;
    }

    const prefersReducedMotion =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Clears first so identical strings re-announce (some screen readers skip duplicates).
    function announce(msg) {
        const live = document.querySelector('[data-pin-announcer]');
        if (!live) return;
        live.textContent = '';
        setTimeout(function () {
            live.textContent = msg;
        }, 50);
    }

    fetchPins(cfg)
        .then(function (pins) {
            if (!pins.length) {
                console.info('[food-map] No pins matched filter tag:hash-food-map.');
                updateCount(0);
                renderFilters([]);
                return;
            }
            // Longest names render first so shorter labels stack on top — a buried "Ari's" vanishes, a buried long name still peeks out.
            pins.slice()
                .sort(function (a, b) {
                    return (b.name || '').length - (a.name || '').length;
                })
                .forEach(function (pin) {
                    const marker = renderPin(map, pin);
                    pinRecords.push({pin: pin, marker: marker});
                });
            // Post slugs are canonical (Ghost-unique, frozen); name slugs are aliases and never shadow them.
            pins.forEach(function (pin) {
                pinBySlug.set(pin.slug, pin);
            });
            pins.forEach(function (pin) {
                const taken = pinBySlug.get(pin.pinSlug);
                if (!taken) pinBySlug.set(pin.pinSlug, pin);
                else if (taken !== pin)
                    console.warn(
                        '[food-map] ' +
                            pin.slug +
                            ': ?pin=' +
                            pin.pinSlug +
                            ' already resolves to ' +
                            taken.slug +
                            '; this pin is reachable only via its post slug.',
                    );
            });

            // Restore filter state from URL BEFORE chips render so the active state paints in one pass.
            const restored = hydrateFiltersFromUrl(urlState.initial, pins);
            filterState.cuisine = restored.cuisine;
            filterState.area = restored.area;
            filterState.price = restored.price;
            filterState.isNew = restored.isNew;
            filterState.searchQuery = restored.searchQuery;

            renderFilters(pins);
            syncChipsToState();
            const inputEl = document.querySelector('[data-search-input]');
            if (restored.searchQuery && inputEl) {
                inputEl.value = restored.searchQuery;
                const cellEl = document.querySelector('[data-search-cell]');
                const wrapEl = document.querySelector('[data-search-wrap]');
                if (cellEl) cellEl.classList.add('is-open');
                if (wrapEl) wrapEl.setAttribute('aria-hidden', 'false');
                inputEl.removeAttribute('tabindex');
                const clearEl = document.querySelector('[data-search-clear]');
                if (clearEl) clearEl.removeAttribute('tabindex');
            }
            applyFilters();

            const activeFilterCount =
                filterState.cuisine.size +
                filterState.area.size +
                filterState.price.size +
                (filterState.isNew ? 1 : 0);
            if (activeFilterCount > 0) announceActiveFilters();

            // Hydrate the deep-link BEFORE fitToPins so the requested pin's flyTo wins; open even if filtered out.
            const requestedSlug = urlState.initial.get('pin');
            const requestedPin = requestedSlug ? pinBySlug.get(requestedSlug) : null;
            if (requestedPin) {
                showPanel(requestedPin, {fromUrl: true});
            } else {
                if (requestedSlug) {
                    console.info(
                        '[food-map] URL ?pin=' +
                            requestedSlug +
                            ' did not match any pin; ignoring.',
                    );
                    urlState.setPin(null);
                }
                // URL filters frame their matching pins; a single pin keeps the default Berlin zoom for context.
                if (activeFilterCount > 0 || filterState.searchQuery) {
                    fitToVisible(0);
                } else if (pins.length > 1) {
                    fitToPins(map, pins);
                }
            }
        })
        .catch(function (err) {
            console.error('[food-map] Pin fetch failed:', err);
        });

    function fetchPins(cfg) {
        const fields = [
            'id',
            'title',
            'slug',
            'url',
            'excerpt',
            'custom_excerpt',
            'feature_image',
            'codeinjection_head',
            'visibility',
        ].join(',');
        const params = [
            'key=' + encodeURIComponent(cfg.contentApiKey),
            'filter=' + encodeURIComponent('tag:hash-food-map'),
            'include=tags,authors',
            'limit=all',
            'fields=' + fields,
        ].join('&');
        return fetch(cfg.apiUrl + '/posts/?' + params)
            .then(function (r) {
                if (!r.ok) throw new Error('Content API HTTP ' + r.status);
                return r.json();
            })
            .then(function (data) {
                return (data.posts || []).map(parsePin).filter(Boolean);
            });
    }

    function parsePin(post) {
        const geo = parseGeo(post.codeinjection_head, post.slug);
        if (!geo) return null;
        const tags = post.tags || [];
        // Cuisines is an array in editor tag order — first entry drives the marker colour.
        const cuisines = tagsWithPrefix(tags, 'hash-cuisine-').map(function (t) {
            return {slug: t.slug, name: t.name};
        });
        const areaTag = tagWithPrefix(tags, 'hash-area-');
        const priceTag = tagWithPrefix(tags, 'hash-price-');
        const isNew = tags.some(function (t) {
            return t.slug === 'hash-new';
        });
        const pinSlug = slugifyName(geo.name);
        if (!pinSlug) {
            console.warn(
                '[food-map] ' +
                    post.slug +
                    ': "name" slugifies to nothing; using the post slug for ?pin=.',
            );
        }
        return {
            slug: post.slug,
            pinSlug: pinSlug || post.slug,
            name: geo.name,
            blurb: pinBlurb(geo.blurb, post),
            url: post.url,
            image: post.feature_image,
            lat: geo.lat,
            lng: geo.lng,
            address: geo.address,
            cuisines: cuisines,
            area: areaTag ? areaTag.slug : null,
            areaName: areaTag ? areaTag.name : null,
            price: priceTag ? priceTag.slug : null,
            isNew: isNew,
        };
    }

    // ?pin= uses the restaurant name, not the post slug; scripts.js builds byline CTAs with the same shape.
    function slugifyName(name) {
        return String(name)
            .toLowerCase()
            .normalize('NFKD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    }

    // Pin-JSON blurb contract: "excerpt" (default) and "headline" pick the post field; any other string renders verbatim.
    function pinBlurb(blurb, post) {
        if (blurb === 'headline') return post.title;
        if (typeof blurb === 'string' && blurb.trim() && blurb !== 'excerpt') return blurb.trim();
        return post.custom_excerpt || post.excerpt || '';
    }

    function tagWithPrefix(tags, prefix) {
        return tags.find(function (t) {
            return t.slug && t.slug.indexOf(prefix) === 0;
        });
    }
    function tagsWithPrefix(tags, prefix) {
        return tags.filter(function (t) {
            return t.slug && t.slug.indexOf(prefix) === 0;
        });
    }

    // codeinjection_head is raw HTML; the pin JSON lives in <script type="application/json" id="food-map-pin">.
    function parseGeo(headHtml, slug) {
        if (!headHtml) {
            console.warn(
                '[food-map] ' +
                    slug +
                    ': Code Injection (Post header) is empty. Paste the <script type="application/json" id="food-map-pin"> block there — see docs/food-map-pin-authoring.md.',
            );
            return null;
        }
        const tmp = document.createElement('div');
        tmp.innerHTML = headHtml;
        const script = tmp.querySelector('script#food-map-pin');
        if (!script) {
            console.warn(
                '[food-map] ' +
                    slug +
                    ': no <script id="food-map-pin"> found in Code Injection. Did you paste it in the post body instead?',
            );
            return null;
        }
        let data;
        try {
            data = JSON.parse(script.textContent);
        } catch (e) {
            console.warn(
                '[food-map] ' +
                    slug +
                    ': JSON.parse failed — check for smart quotes, trailing commas, or decimal commas.',
                e.message,
            );
            return null;
        }
        if (typeof data.lat !== 'number' || typeof data.lng !== 'number') {
            console.warn(
                '[food-map] ' + slug + ': lat/lng must be unquoted numbers. Got:',
                JSON.stringify(data),
            );
            return null;
        }
        if (typeof data.name !== 'string' || !data.name.trim()) {
            console.warn(
                '[food-map] ' +
                    slug +
                    ': JSON is missing "name" (the restaurant name) — pin skipped. See docs/food-map-pin-authoring.md.',
            );
            return null;
        }
        if (typeof data.address !== 'string' || !data.address.trim()) {
            console.warn(
                '[food-map] ' +
                    slug +
                    ': JSON is missing "address" — pin skipped. See docs/food-map-pin-authoring.md.',
            );
            return null;
        }
        return data;
    }

    function renderPin(map, pin) {
        const el = document.createElement('button');
        el.className = 'food-map__marker' + (pin.isNew ? ' food-map__marker--new' : '');
        el.type = 'button';
        el.setAttribute('aria-label', pin.name);
        // --marker-on-color is the WCAG-safe text on --marker-color for the hover/focus label fill.
        const primaryCuisine = pin.cuisines[0];
        const entry = primaryCuisine ? colorFromSlug(primaryCuisine.slug) : null;
        if (entry) {
            el.style.setProperty('--marker-color', entry.bg);
            el.style.setProperty('--marker-on-color', entry.on);
        }
        el.innerHTML =
            '<span class="food-map__marker-dot" aria-hidden="true"></span>' +
            '<span class="food-map__marker-label">' +
            escapeHtml(pin.name) +
            '</span>';

        // stopPropagation keeps the map's click handler from instantly closing the panel.
        el.addEventListener('click', function (e) {
            e.stopPropagation();
            showPanel(pin);
        });

        // anchor:'left' puts the marker's left edge at the lat/lng — close enough to the dot centre at city zoom.
        const marker = new mapboxgl.Marker({element: el, anchor: 'left'})
            .setLngLat([pin.lng, pin.lat])
            .addTo(map);
        // Mapbox sets role="img" on markers; invalid on <button> (axe), so strip it.
        el.removeAttribute('role');
        return marker;
    }

    function fitToPins(map, pins) {
        const bounds = new mapboxgl.LngLatBounds();
        pins.forEach(function (p) {
            bounds.extend([p.lng, p.lat]);
        });
        map.fitBounds(bounds, {padding: 60, maxZoom: 14, duration: 0});
    }

    // Frames the pins that survive the current filters; no-op when nothing matches.
    function fitToVisible(duration) {
        const visible = pinRecords
            .filter(function (rec) {
                return matchesFilters(rec.pin);
            })
            .map(function (rec) {
                return rec.pin;
            });
        if (visible.length > 1) {
            const bounds = new mapboxgl.LngLatBounds();
            visible.forEach(function (p) {
                bounds.extend([p.lng, p.lat]);
            });
            map.fitBounds(bounds, {padding: 60, maxZoom: 14, duration: duration});
        } else if (visible.length === 1) {
            map.flyTo({center: [visible[0].lng, visible[0].lat], zoom: 14, duration: duration});
        }
    }

    function updateCount(n) {
        const el = document.querySelector('.food-map__count strong');
        if (el) el.textContent = String(n);
        // Mirror in the mobile filter modal's "Show N results" CTA so it updates live as chips toggle.
        const modalCount = document.querySelector('[data-visible-count]');
        if (modalCount) modalCount.textContent = String(n);
    }

    // ── Detail panel ─────────────────────────────────────────────────
    // modeBeforePanel: closing returns the user to the view (list or map) they opened from.
    const panel = document.querySelector('[data-pin-panel]');
    const panelClose = panel ? panel.querySelector('[data-panel-close]') : null;
    const panelCopy = panel ? panel.querySelector('[data-panel-copy]') : null;
    let copyResetTimer = 0;
    let modeBeforePanel = null;

    // Markers stack in DOM order; the selected pin's label gets a z-index lift so neighbours can't cover it.
    let selectedMarkerEl = null;
    function setSelectedMarker(pin) {
        if (selectedMarkerEl) selectedMarkerEl.classList.remove('is-selected');
        selectedMarkerEl = null;
        if (!pin) return;
        const rec = pinRecords.find(function (r) {
            return r.pin === pin;
        });
        if (rec) {
            selectedMarkerEl = rec.marker.getElement();
            selectedMarkerEl.classList.add('is-selected');
        }
    }

    function showPanel(pin, opts) {
        if (!panel) return;
        opts = opts || {};
        populatePanel(pin);
        setSelectedMarker(pin);
        panel.removeAttribute('hidden');
        // Force reflow so the slide-in transition runs from the hidden state.
        void panel.offsetHeight;
        panel.classList.add('is-open');
        flyToPin(pin);
        urlState.setPin(pin.pinSlug);
        // URL-driven opens announce and move focus into the panel; click opens keep focus on the trigger.
        if (opts.fromUrl) {
            announce('Showing details for ' + pin.name);
            const titleEl = panel.querySelector('[data-panel-title]');
            if (titleEl) {
                titleEl.setAttribute('tabindex', '-1');
                setTimeout(function () {
                    titleEl.focus();
                }, 50);
            }
        }
    }

    function hidePanel() {
        if (!panel) return;
        const wasOpen = panel.classList.contains('is-open');
        setSelectedMarker(null);
        panel.classList.remove('is-open');
        // Re-hide from a11y tree after the slide-out transition completes.
        setTimeout(function () {
            if (!panel.classList.contains('is-open')) panel.setAttribute('hidden', '');
        }, 300);
        urlState.setPin(null);
        // Zoom back out only if something was open; background map clicks call this too.
        if (wasOpen) {
            const currentZoom = map.getZoom();
            if (currentZoom > DEFAULT_ZOOM) {
                map.flyTo({
                    zoom: Math.max(DEFAULT_ZOOM, currentZoom - 2),
                    duration: 600,
                });
            }
        }
        if (modeBeforePanel === 'list' && mapArea && !mapArea.classList.contains('is-list-view')) {
            mapArea.classList.add('is-list-view');
            if (listEl) listEl.removeAttribute('hidden');
            setListButton(true);
            renderList();
        }
        modeBeforePanel = null;
    }

    function populatePanel(pin) {
        // Tint the eyebrow + Read-the-review CTA with the primary cuisine colour.
        const primaryCuisine = pin.cuisines[0];
        const entry = primaryCuisine ? colorFromSlug(primaryCuisine.slug) : null;
        if (entry) {
            panel.style.setProperty('--panel-color', entry.bg);
            panel.style.setProperty('--panel-on-color', entry.on);
        } else {
            panel.style.removeProperty('--panel-color');
            panel.style.removeProperty('--panel-on-color');
        }

        const imageEl = panel.querySelector('[data-panel-image]');
        const imageWrap = panel.querySelector('.food-map__panel-image-wrap');
        if (imageEl && pin.image) {
            imageWrap.removeAttribute('data-empty');
            // data-loading hides the previous pin's photo; on slow connections it otherwise lingers under the new pin's text.
            imageWrap.setAttribute('data-loading', '');
            imageEl.alt = pin.name;
            imageEl.onload = function () {
                imageWrap.removeAttribute('data-loading');
            };
            imageEl.onerror = function () {
                imageWrap.removeAttribute('data-loading');
                imageWrap.setAttribute('data-empty', '');
                imageEl.removeAttribute('src');
            };
            imageEl.src = pin.image;
            // Cache hit resolves synchronously — no load event race.
            if (imageEl.complete && imageEl.naturalWidth) {
                imageWrap.removeAttribute('data-loading');
            }
        } else if (imageWrap) {
            imageWrap.removeAttribute('data-loading');
            imageWrap.setAttribute('data-empty', '');
            if (imageEl) imageEl.removeAttribute('src');
        }

        // "New" hardcodes le jaune vif (#f4bd48) to match the marker star; primary cuisine still drives --panel-color.
        const eyebrowEl = panel.querySelector('[data-panel-eyebrow]');
        if (eyebrowEl) {
            let html = '';
            if (pin.isNew) {
                html +=
                    '<button type="button" class="food-map__panel-cuisine-tag food-map__panel-cuisine-tag--new" style="--cuisine-color:#f4bd48;--cuisine-on-color:#0e2d58" data-filter-axis="isNew" data-filter-value="new" aria-label="Filter map by new restaurants">' +
                    '<span class="food-map__panel-cuisine-dot" aria-hidden="true"></span>' +
                    'New' +
                    '</button>';
            }
            html += pin.cuisines
                .map(function (c) {
                    const cEntry = colorFromSlug(c.slug);
                    const label = escapeHtml(formatAxisLabel(c.name, c.slug, 'cuisine'));
                    const color = cEntry ? cEntry.bg : 'currentColor';
                    const style =
                        '--cuisine-color:' +
                        color +
                        (cEntry ? ';--cuisine-on-color:' + cEntry.on : '');
                    return (
                        '<button type="button" class="food-map__panel-cuisine-tag" style="' +
                        style +
                        '" data-filter-axis="cuisine" data-filter-value="' +
                        escapeAttr(c.slug) +
                        '" aria-label="Filter map by ' +
                        label +
                        '">' +
                        '<span class="food-map__panel-cuisine-dot" aria-hidden="true"></span>' +
                        label +
                        '</button>'
                    );
                })
                .join('');
            eyebrowEl.innerHTML = html;
            eyebrowEl.querySelectorAll('[data-filter-axis]').forEach(function (btn) {
                btn.addEventListener('click', function () {
                    filterFromPanel(btn.dataset.filterAxis, btn.dataset.filterValue);
                });
            });
        }
        setText(panel, '[data-panel-title]', pin.name || '');

        if (panelCopy) {
            panelCopy.setAttribute(
                'aria-label',
                pin.name ? 'Copy link to ' + pin.name : 'Copy link to this restaurant',
            );
            // Reset stale "Copied" state from the previous pin.
            panelCopy.classList.remove('is-copied');
            if (copyResetTimer) {
                clearTimeout(copyResetTimer);
                copyResetTimer = 0;
            }
        }

        const metaParts = [];
        if (pin.area) metaParts.push(formatAxisLabel(pin.areaName, pin.area, 'area'));
        if (pin.price) metaParts.push('€'.repeat(priceLevel(pin.price)));
        if (pin.address) metaParts.push(pin.address);
        setText(panel, '[data-panel-meta]', metaParts.join(' · '));

        setText(panel, '[data-panel-excerpt]', pin.blurb || '');

        // Google Maps universal URL — opens Apple Maps prompt on iOS, opens in browser on desktop.
        const directionsEl = panel.querySelector('[data-panel-directions]');
        if (directionsEl) {
            directionsEl.href =
                'https://www.google.com/maps/dir/?api=1&destination=' +
                encodeURIComponent(pin.lat + ',' + pin.lng);
        }
        const readEl = panel.querySelector('[data-panel-read]');
        if (readEl) readEl.href = pin.url;
    }

    function setText(root, selector, text) {
        const el = root.querySelector(selector);
        if (el) el.textContent = text;
    }

    function copyUrlToClipboard(triggerEl, successMsg) {
        const url = window.location.href;
        function onSuccess() {
            if (triggerEl) {
                triggerEl.classList.add('is-copied');
                setTimeout(function () {
                    triggerEl.classList.remove('is-copied');
                }, 1800);
            }
            announce(successMsg || 'Link copied');
        }
        function onFailure(err) {
            console.warn('[food-map] Clipboard copy failed:', err);
            announce('Could not copy link; copy the URL from your browser bar.');
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(url).then(onSuccess, onFailure);
            return;
        }
        // Legacy fallback for http:// or older browsers.
        try {
            const ta = document.createElement('textarea');
            ta.value = url;
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.select();
            const ok = document.execCommand('copy');
            document.body.removeChild(ta);
            if (ok) onSuccess();
            else onFailure(new Error('execCommand returned false'));
        } catch (err) {
            onFailure(err);
        }
    }

    function copyPanelLink() {
        if (!panelCopy) return;
        copyUrlToClipboard(panelCopy, 'Link copied to clipboard');
    }

    // Add-only: never toggles an active filter off.
    function filterFromPanel(axis, value) {
        const chip = document.querySelector(
            '.food-map__chip[data-axis="' + axis + '"][data-value="' + value + '"]',
        );
        if (chip && !chip.classList.contains('is-active')) chip.click();
        hidePanel();
        fitToVisible(prefersReducedMotion ? 0 : 600);
        if (chip) announce('Filtering by ' + chip.textContent.trim() + '.');
    }

    const shareBtn = document.querySelector('[data-action-share]');
    if (shareBtn) {
        shareBtn.addEventListener('click', function () {
            copyUrlToClipboard(shareBtn, 'Map link copied to clipboard');
        });
    }

    function flyToPin(pin) {
        const targetZoom = Math.max(map.getZoom(), 14);
        // Offset clears the right-docked desktop panel; the mobile panel covers the map, so skip offset and animation.
        const isMobile = window.innerWidth <= 768;
        map.flyTo({
            center: [pin.lng, pin.lat],
            zoom: targetZoom,
            duration: prefersReducedMotion || isMobile ? 0 : 700,
            offset: isMobile ? [0, 0] : [-200, 0],
        });
    }

    if (panelClose) {
        panelClose.addEventListener('click', hidePanel);
    }
    if (panelCopy) {
        panelCopy.addEventListener('click', copyPanelLink);
    }
    // Empty-map click closes the panel (marker clicks stopPropagation, so this only fires on the canvas itself).
    map.on('click', hidePanel);
    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') hidePanel();
    });

    // ── Mobile filters modal ─────────────────────────────────────────
    // Same chip DOM as desktop, reflowed by CSS; .is-filters-open on .food-map__member drives visibility.
    const memberSection = document.querySelector('.food-map__member');
    const filtersBtn = document.querySelector('[data-action-filters]');
    const filtersCountEl = document.querySelector('[data-filters-count]');
    const filterModalTitle = document.getElementById('filter-modal-title');

    function updateFiltersBadge() {
        if (!filtersCountEl) return;
        const n =
            filterState.cuisine.size +
            filterState.area.size +
            filterState.price.size +
            (filterState.isNew ? 1 : 0);
        if (n > 0) {
            filtersCountEl.textContent = String(n);
            filtersCountEl.removeAttribute('hidden');
        } else {
            filtersCountEl.setAttribute('hidden', '');
        }
    }

    // Snapshot at modal open; close only auto-fits the map if filters actually changed.
    let filterStateSnapshot = '';

    function snapshotFilterState() {
        return JSON.stringify({
            cuisine: Array.from(filterState.cuisine).sort(),
            area: Array.from(filterState.area).sort(),
            price: Array.from(filterState.price).sort(),
            isNew: filterState.isNew,
        });
    }

    function openFilterModal() {
        if (!memberSection) return;
        memberSection.classList.add('is-filters-open');
        filterStateSnapshot = snapshotFilterState();
        if (filtersBtn) {
            filtersBtn.setAttribute('aria-expanded', 'true');
            filtersBtn.setAttribute('aria-label', 'Hide filters');
        }
        // Move focus into the sheet so SR users land at the modal title.
        if (filterModalTitle) setTimeout(() => filterModalTitle.focus(), 50);
    }

    function closeFilterModal() {
        if (!memberSection || !memberSection.classList.contains('is-filters-open')) return;
        memberSection.classList.remove('is-filters-open');
        if (filtersBtn) {
            filtersBtn.setAttribute('aria-expanded', 'false');
            filtersBtn.setAttribute('aria-label', 'Show filters');
            filtersBtn.focus();
        }
        // Fit to the surviving pins so a narrowed filter doesn't leave an empty map; skip if a panel is open.
        const changed = filterStateSnapshot !== snapshotFilterState();
        filterStateSnapshot = '';
        if (!changed) return;
        if (!isMobileViewport()) return;
        if (panel && panel.classList.contains('is-open')) return;
        fitToVisible(prefersReducedMotion ? 0 : 600);
    }

    if (filtersBtn) {
        filtersBtn.addEventListener('click', openFilterModal);
    }
    document.querySelectorAll('[data-filter-modal-close]').forEach(function (el) {
        el.addEventListener('click', closeFilterModal);
    });
    document.addEventListener('keydown', function (e) {
        if (
            e.key === 'Escape' &&
            memberSection &&
            memberSection.classList.contains('is-filters-open')
        ) {
            closeFilterModal();
        }
    });

    // ── Reset action ─────────────────────────────────────────────────
    // data-action-reset is shared by the bar's "Reset" (desktop) and the modal footer's "Clear all" (mobile).
    document.querySelectorAll('[data-action-reset]').forEach(function (btn) {
        btn.addEventListener('click', resetFilters);
    });

    function resetFilters() {
        filterState.cuisine.clear();
        filterState.area.clear();
        filterState.price.clear();
        filterState.isNew = false;
        filterState.searchQuery = '';
        const allChips = document.querySelectorAll('.food-map__chip.is-active');
        allChips.forEach(function (chip) {
            chip.classList.remove('is-active');
            chip.setAttribute('aria-pressed', 'false');
        });
        if (searchInput) {
            searchInput.value = '';
            collapseSearch();
        }
        // Undo the active-first chip reordering.
        const filterRow = document.querySelector('[data-filter-row]');
        if (filterRow) {
            ['cuisine', 'area'].forEach(function (axis) {
                reorderAxisChips(filterRow, axis);
            });
        }
        applyFilters();
        hidePanel();
        map.flyTo({
            center: BERLIN_CENTER,
            zoom: DEFAULT_ZOOM,
            duration: 600,
        });
    }

    // ── Search ───────────────────────────────────────────────────────
    // Trigger and expanded input share the cell; .is-open swaps them via parallel CSS transitions.
    const searchCell = document.querySelector('[data-search-cell]');
    const searchBtn = document.querySelector('[data-action-search]');
    const searchWrap = document.querySelector('[data-search-wrap]');
    const searchInput = document.querySelector('[data-search-input]');
    const searchClear = document.querySelector('[data-search-clear]');

    function expandSearch() {
        if (!searchCell || !searchInput || !searchWrap) return;
        searchCell.classList.add('is-open');
        searchWrap.setAttribute('aria-hidden', 'false');
        if (searchInput) searchInput.removeAttribute('tabindex');
        if (searchClear) searchClear.removeAttribute('tabindex');
        // Focus after the wrap is interactive (post-transition start); 60ms covers the opacity delay.
        setTimeout(function () {
            searchInput.focus();
        }, 60);
    }

    function collapseSearch() {
        if (!searchCell || !searchWrap) return;
        searchCell.classList.remove('is-open');
        searchWrap.setAttribute('aria-hidden', 'true');
        if (searchInput) searchInput.setAttribute('tabindex', '-1');
        if (searchClear) searchClear.setAttribute('tabindex', '-1');
    }

    let searchFitTimer = null;

    function clearSearchInput() {
        if (!searchInput) return;
        clearTimeout(searchFitTimer);
        searchInput.value = '';
        filterState.searchQuery = '';
        applyFilters();
        fitToVisible(prefersReducedMotion ? 0 : 600);
    }
    function clearAndCollapseSearch() {
        clearSearchInput();
        collapseSearch();
    }

    function isMobileViewport() {
        return window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
    }

    if (searchBtn) {
        searchBtn.addEventListener('click', expandSearch);
    }
    if (searchInput) {
        searchInput.addEventListener('input', function (e) {
            filterState.searchQuery = (e.target.value || '').toLowerCase().trim();
            applyFilters();
            // Debounced so the map settles once per typing pause, not per keystroke.
            clearTimeout(searchFitTimer);
            searchFitTimer = setTimeout(function () {
                fitToVisible(prefersReducedMotion ? 0 : 600);
            }, 350);
        });
        searchInput.addEventListener('keydown', function (e) {
            if (e.key === 'Escape') {
                clearAndCollapseSearch();
                if (searchBtn) searchBtn.focus();
            }
        });
        // Blur must not wipe the query — map drags and keyboard dismissal both blur the input; only an empty search collapses.
        searchInput.addEventListener('blur', function () {
            setTimeout(function () {
                if (!searchInput.value) collapseSearch();
            }, 0);
        });
    }
    if (searchClear) {
        // preventDefault keeps the input focused; a blur here would race the collapse.
        searchClear.addEventListener('mousedown', function (e) {
            e.preventDefault();
        });
        // X mid-typing clears for a fresh query; clicked with focus elsewhere it clears and closes.
        searchClear.addEventListener('click', function () {
            const wasTyping = document.activeElement === searchInput;
            clearSearchInput();
            if (!wasTyping) collapseSearch();
        });
    }

    // ── List view ────────────────────────────────────────────────────
    const listBtn = document.querySelector('[data-action-list]');
    const listEl = document.querySelector('[data-pin-list]');
    const listCardsEl = document.querySelector('[data-list-cards]');
    const mapArea = document.querySelector('.food-map__map-area');
    let listSort = 'price';
    let listSortDir = 'asc';

    // Label span only — textContent would wipe the icon pair; .is-list drives the icon swap in CSS.
    function setListButton(isList) {
        if (!listBtn) return;
        listBtn.classList.toggle('is-list', isList);
        const label = listBtn.querySelector('[data-list-label]');
        if (label) label.textContent = isList ? 'Map View' : 'List View';
        listBtn.setAttribute('aria-label', isList ? 'Show map view' : 'Show list view');
    }

    function updateSortButtons() {
        document.querySelectorAll('[data-sort]').forEach(function (b) {
            const active = b.dataset.sort === listSort;
            b.classList.toggle('is-active', active);
            b.setAttribute('aria-pressed', String(active));
            const arrow = b.querySelector('.food-map__list-sort-arrow');
            if (arrow) arrow.textContent = listSortDir === 'asc' ? '↑' : '↓';
            // Flip the alpha label's leading text node without touching the arrow span.
            if (b.dataset.sort === 'alpha' && b.firstChild && b.firstChild.nodeType === 3) {
                b.firstChild.nodeValue = active && listSortDir === 'desc' ? 'Z–A ' : 'A–Z ';
            }
        });
    }

    document.querySelectorAll('[data-sort]').forEach(function (btn) {
        btn.addEventListener('click', function () {
            if (btn.dataset.sort === listSort) {
                listSortDir = listSortDir === 'asc' ? 'desc' : 'asc';
            } else {
                listSort = btn.dataset.sort;
                listSortDir = 'asc';
            }
            updateSortButtons();
            renderList();
        });
    });

    if (listBtn) {
        listBtn.addEventListener('click', toggleListView);
    }

    function toggleListView() {
        if (!mapArea || !listBtn || !listEl) return;
        // Null modeBeforePanel first so hidePanel doesn't restore the view being left.
        if (panel && panel.classList.contains('is-open')) {
            modeBeforePanel = null;
            hidePanel();
        }
        const isList = mapArea.classList.toggle('is-list-view');
        setListButton(isList);
        if (isList) {
            listEl.removeAttribute('hidden');
            renderList();
        } else {
            listEl.setAttribute('hidden', '');
        }
    }

    function renderList() {
        if (!listCardsEl) return;
        listCardsEl.innerHTML = '';
        const visible = pinRecords.filter(function (rec) {
            return matchesFilters(rec.pin);
        });
        if (!visible.length) {
            const empty = document.createElement('p');
            empty.className = 'food-map__list-empty';
            empty.textContent = 'No restaurants match the current filters.';
            listCardsEl.appendChild(empty);
            return;
        }
        const flip = listSortDir === 'desc' ? -1 : 1;
        if (listSort === 'alpha') {
            visible.sort(function (a, b) {
                return flip * (a.pin.name || '').localeCompare(b.pin.name || '');
            });
        } else {
            visible.sort(function (a, b) {
                const ap = a.pin.price ? priceLevel(a.pin.price) : 99;
                const bp = b.pin.price ? priceLevel(b.pin.price) : 99;
                if (ap !== bp) return flip * (ap - bp);
                return flip * (a.pin.name || '').localeCompare(b.pin.name || '');
            });
        }
        visible.forEach(function (rec) {
            listCardsEl.appendChild(makeListCard(rec.pin));
        });
    }

    function makeListCard(pin) {
        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'food-map__list-card';
        const primaryCuisine = pin.cuisines[0];
        const entry = primaryCuisine ? colorFromSlug(primaryCuisine.slug) : null;
        if (entry) card.style.setProperty('--cuisine-color', entry.bg);

        const cuisineLabels = pin.cuisines
            .map(function (c) {
                return formatAxisLabel(c.name, c.slug, 'cuisine');
            })
            .join(', ');
        const metaParts = [];
        if (cuisineLabels) metaParts.push(cuisineLabels);
        if (pin.area) metaParts.push(formatAxisLabel(pin.areaName, pin.area, 'area'));
        if (pin.price) metaParts.push('€'.repeat(priceLevel(pin.price)));

        const image = pin.image
            ? '<img class="food-map__list-card-image" src="' +
              escapeAttr(pin.image) +
              '" alt="" loading="lazy" />'
            : '';
        const newPill = pin.isNew ? '<span class="food-map__list-card-new">New</span>' : '';

        card.innerHTML =
            image +
            '<div class="food-map__list-card-body">' +
            '<div class="food-map__list-card-title-row">' +
            // h2 (not h3) so the heading order goes h1 page title → h2 restaurant — axe flags h1→h3 jumps.
            '<h2 class="food-map__list-card-title">' +
            escapeHtml(pin.name) +
            '</h2>' +
            newPill +
            '</div>' +
            '<p class="food-map__list-card-meta">' +
            escapeHtml(metaParts.join(' · ')) +
            '</p>' +
            '<p class="food-map__list-card-excerpt">' +
            escapeHtml(pin.blurb || '') +
            '</p>' +
            '</div>';

        card.addEventListener('click', function () {
            // modeBeforePanel = 'list' tells hidePanel() to return the user to the list view on close.
            if (mapArea.classList.contains('is-list-view')) {
                modeBeforePanel = 'list';
                mapArea.classList.remove('is-list-view');
                if (listEl) listEl.setAttribute('hidden', '');
                setListButton(false);
            }
            showPanel(pin);
        });
        return card;
    }

    // ── Filters ──────────────────────────────────────────────────────
    // AND across axes; OR (multi-select) within an axis.

    // Chips visible before "+N": desktop 2 keeps the strip tight; mobile 6 avoids burying the other axes.
    const mobileLayoutQuery = window.matchMedia ? window.matchMedia('(max-width: 768px)') : null;
    function isMobileLayout() {
        return !!(mobileLayoutQuery && mobileLayoutQuery.matches);
    }
    function getVisibleLimits() {
        return isMobileLayout() ? {cuisine: 6, area: 6} : {cuisine: 2, area: 2};
    }

    function renderFilters(pins) {
        const VISIBLE_LIMITS = getVisibleLimits();
        const row = document.querySelector('[data-filter-row]');
        if (!row) return;
        row.innerHTML = '';
        // Strip any is-expanded-* state from a previous render.
        row.className = 'food-map__filters';

        const cuisines = buildAxis(pins, 'cuisine', 'cuisineName');
        const areas = buildAxis(pins, 'area', 'areaName');
        const prices = buildAxis(pins, 'price', null);
        const newCount = pins.filter(function (p) {
            return p.isNew;
        }).length;

        // Per-axis containers so wrapping can't split a label from its chips (axe-flagged).
        if (cuisines.length) {
            const group = makeAxisGroup('cuisine');
            group.appendChild(makeFilterLabel('Cuisine', 'cuisine'));
            cuisines.forEach(function (entry, idx) {
                const colorEntry = colorFromSlug(entry.slug);
                const chip = makeChip({
                    axis: 'cuisine',
                    value: entry.slug,
                    label: entry.label,
                    variant: 'cuisine',
                    color: colorEntry ? colorEntry.bg : null,
                    onColor: colorEntry ? colorEntry.on : null,
                });
                chip.dataset.originalIndex = String(idx);
                if (idx >= VISIBLE_LIMITS.cuisine) chip.classList.add('food-map__chip--overflow');
                group.appendChild(chip);
            });
            if (cuisines.length > VISIBLE_LIMITS.cuisine) {
                group.appendChild(
                    makeOverflowToggle('cuisine', cuisines.length - VISIBLE_LIMITS.cuisine, row),
                );
            }
            row.appendChild(group);
        }

        if (areas.length) {
            const group = makeAxisGroup('area');
            group.appendChild(makeFilterLabel('District', 'area'));
            areas.forEach(function (entry, idx) {
                const chip = makeChip({
                    axis: 'area',
                    value: entry.slug,
                    label: entry.label,
                    variant: 'cerulean',
                });
                chip.dataset.originalIndex = String(idx);
                if (idx >= VISIBLE_LIMITS.area) chip.classList.add('food-map__chip--overflow');
                group.appendChild(chip);
            });
            if (areas.length > VISIBLE_LIMITS.area) {
                group.appendChild(
                    makeOverflowToggle('area', areas.length - VISIBLE_LIMITS.area, row),
                );
            }
            row.appendChild(group);
        }

        if (prices.length) {
            // JS-driven break (see updateFilterBreak); only fires when the New axis would orphan onto its own row.
            if (row.children.length) {
                const breakEl = document.createElement('span');
                breakEl.className = 'food-map__filter-break';
                breakEl.setAttribute('aria-hidden', 'true');
                row.appendChild(breakEl);
            }
            const group = makeAxisGroup('price');
            group.appendChild(makeFilterLabel('Price', 'price'));
            prices.forEach(function (entry) {
                group.appendChild(
                    makeChip({
                        axis: 'price',
                        value: entry.slug,
                        label: entry.label,
                    }),
                );
            });
            row.appendChild(group);
        }

        if (newCount) {
            const group = makeAxisGroup('isNew');
            // Label is plain text; the yellow star is rendered as a ::before pseudo-element via .food-map__chip--new.
            group.appendChild(
                makeChip({
                    axis: 'isNew',
                    value: 'new',
                    label: 'New',
                    variant: 'new',
                }),
            );
            row.appendChild(group);
        }
        // Measure once browser flow has settled — schedules a frame so layout is current.
        scheduleFilterBreakUpdate();
    }

    function makeAxisGroup(axis) {
        const group = document.createElement('div');
        group.className = 'food-map__filter-axis';
        group.dataset.axis = axis;
        return group;
    }

    // Toggles .is-force-break when the New axis would otherwise orphan onto its own row.
    function updateFilterBreak() {
        const row = document.querySelector('[data-filter-row]');
        if (!row) return;
        // Mobile modal stacks axes vertically; the offsetTop probe always reads "orphaned" there.
        if (isMobileLayout()) {
            row.classList.remove('is-force-break');
            return;
        }
        const isNewAxis = row.querySelector('.food-map__filter-axis[data-axis="isNew"]');
        const priceAxis = row.querySelector('.food-map__filter-axis[data-axis="price"]');
        if (!isNewAxis || !priceAxis) {
            row.classList.remove('is-force-break');
            return;
        }
        // Measure natural layout with the break collapsed — re-add it only if the New axis sat alone on its row.
        const wasForced = row.classList.contains('is-force-break');
        row.classList.remove('is-force-break');
        const orphaned = isNewAxis.offsetTop > priceAxis.offsetTop;
        if (orphaned) row.classList.add('is-force-break');
        else if (wasForced) row.classList.remove('is-force-break');
    }

    let filterBreakRaf = 0;
    function scheduleFilterBreakUpdate() {
        if (filterBreakRaf) return;
        filterBreakRaf = window.requestAnimationFrame(function () {
            filterBreakRaf = 0;
            updateFilterBreak();
        });
    }
    window.addEventListener('resize', scheduleFilterBreakUpdate);

    function overflowToggleLabel(axis, count) {
        return 'Show ' + count + ' more ' + axis + ' filter' + (String(count) === '1' ? '' : 's');
    }

    function makeOverflowToggle(axis, count, row) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'food-map__chip food-map__chip--overflow-toggle';
        btn.dataset.axis = axis;
        btn.dataset.overflowCount = String(count);
        btn.textContent = '+' + count;
        btn.setAttribute('aria-label', overflowToggleLabel(axis, count));
        btn.setAttribute('aria-expanded', 'false');
        btn.addEventListener('click', function () {
            const expandedClass = 'is-expanded-' + axis;
            const isExpanded = row.classList.contains(expandedClass);
            row.classList.toggle(expandedClass, !isExpanded);
            // dataset, not the closured count — applyOverflowLimits rewrites it on breakpoint change.
            btn.textContent = isExpanded ? '+' + btn.dataset.overflowCount : 'Show Less';
            btn.setAttribute(
                'aria-label',
                isExpanded
                    ? overflowToggleLabel(axis, btn.dataset.overflowCount)
                    : 'Show fewer ' + axis + ' filters',
            );
            btn.setAttribute('aria-expanded', String(!isExpanded));
            // Expanding/collapsing changes row content width — re-evaluate the orphan break.
            scheduleFilterBreakUpdate();
        });
        return btn;
    }

    // Active chips first, then inactive, each block in originalIndex order so reordering stays stable.
    function reorderAxisChips(row, axis) {
        const label = row.querySelector('[data-axis-label="' + axis + '"]');
        if (!label) return;
        const chips = Array.from(
            row.querySelectorAll(
                '.food-map__chip[data-axis="' + axis + '"]:not(.food-map__chip--overflow-toggle)',
            ),
        );
        if (!chips.length) return;
        chips.sort(function (a, b) {
            const aActive = a.classList.contains('is-active');
            const bActive = b.classList.contains('is-active');
            if (aActive !== bActive) return aActive ? -1 : 1;
            return parseInt(a.dataset.originalIndex, 10) - parseInt(b.dataset.originalIndex, 10);
        });
        let anchor = label;
        chips.forEach(function (chip) {
            anchor.parentNode.insertBefore(chip, anchor.nextSibling);
            anchor = chip;
        });
    }

    function collapseAllOverflow(row) {
        Array.from(row.classList).forEach(function (cls) {
            if (cls.indexOf('is-expanded-') === 0) row.classList.remove(cls);
        });
        row.querySelectorAll('.food-map__chip--overflow-toggle').forEach(function (toggle) {
            if (toggle.dataset.overflowCount) {
                toggle.textContent = '+' + toggle.dataset.overflowCount;
                toggle.setAttribute(
                    'aria-label',
                    overflowToggleLabel(toggle.dataset.axis, toggle.dataset.overflowCount),
                );
                toggle.setAttribute('aria-expanded', 'false');
            }
        });
    }

    // Relimit in place on breakpoint change so active chip states survive (no re-render).
    function applyOverflowLimits() {
        const row = document.querySelector('[data-filter-row]');
        if (!row) return;
        const limits = getVisibleLimits();
        collapseAllOverflow(row);
        ['cuisine', 'area'].forEach(function (axis) {
            const chips = row.querySelectorAll(
                '.food-map__chip[data-axis="' + axis + '"]:not(.food-map__chip--overflow-toggle)',
            );
            let hidden = 0;
            Array.prototype.forEach.call(chips, function (chip) {
                const overflow = parseInt(chip.dataset.originalIndex, 10) >= limits[axis];
                chip.classList.toggle('food-map__chip--overflow', overflow);
                if (overflow) hidden++;
            });
            const toggle = row.querySelector(
                '.food-map__chip--overflow-toggle[data-axis="' + axis + '"]',
            );
            if (toggle) {
                toggle.dataset.overflowCount = String(hidden);
                toggle.textContent = '+' + hidden;
                toggle.setAttribute('aria-label', overflowToggleLabel(axis, hidden));
                toggle.hidden = hidden === 0;
            }
        });
    }

    if (mobileLayoutQuery) {
        const onLayoutChange = function () {
            applyOverflowLimits();
            scheduleFilterBreakUpdate();
        };
        if (mobileLayoutQuery.addEventListener)
            mobileLayoutQuery.addEventListener('change', onLayoutChange);
        else if (mobileLayoutQuery.addListener) mobileLayoutQuery.addListener(onLayoutChange);
    }

    document.addEventListener('click', function (e) {
        const row = document.querySelector('[data-filter-row]');
        if (!row || row.contains(e.target)) return;
        collapseAllOverflow(row);
    });

    function buildAxis(pins, slugField, nameField) {
        const accum = new Map();
        pins.forEach(function (pin) {
            if (slugField === 'cuisine') {
                pin.cuisines.forEach(function (c) {
                    if (!accum.has(c.slug)) {
                        accum.set(c.slug, {
                            slug: c.slug,
                            label: formatAxisLabel(c.name, c.slug, 'cuisine'),
                            count: 0,
                        });
                    }
                    accum.get(c.slug).count++;
                });
                return;
            }
            const slug = pin[slugField];
            if (!slug) return;
            if (!accum.has(slug)) {
                const rawName = nameField ? pin[nameField] : null;
                accum.set(slug, {
                    slug: slug,
                    label: formatAxisLabel(rawName, slug, slugField),
                    count: 0,
                });
            }
            accum.get(slug).count++;
        });
        const out = Array.from(accum.values());
        if (slugField === 'price') {
            out.sort(function (a, b) {
                return priceLevel(a.slug) - priceLevel(b.slug);
            });
        } else {
            out.sort(function (a, b) {
                return a.label.localeCompare(b.label);
            });
        }
        return out;
    }

    function priceLevel(slug) {
        const n = parseInt(slug.replace(/^hash-price-/, ''), 10);
        return isNaN(n) ? 99 : n;
    }

    // Prefer the editor-typed tag name (slugs lose umlauts); fall back to the slug suffix.
    function formatAxisLabel(name, slug, slugField) {
        if (slugField === 'price') return '€'.repeat(priceLevel(slug));
        let s = (name || '').trim();
        if (s.startsWith('#')) s = s.slice(1);
        s = s.replace(new RegExp('^' + slugField + '-', 'i'), '');
        if (!s) {
            s = slug.replace(/^hash-/, '').replace(new RegExp('^' + slugField + '-'), '');
        }
        return s
            .split(/[\s-]+/)
            .filter(Boolean)
            .map(function (w) {
                return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
            })
            .join(' ');
    }

    function makeFilterLabel(text, axis) {
        const el = document.createElement('span');
        el.className = 'food-map__filter-label';
        if (axis) el.dataset.axisLabel = axis;
        el.textContent = text;
        return el;
    }

    function makeChip(opts) {
        const el = document.createElement('button');
        el.type = 'button';
        el.className = 'food-map__chip';
        if (opts.variant === 'cuisine') el.classList.add('food-map__chip--cuisine');
        else if (opts.variant === 'cerulean') el.classList.add('food-map__chip--cerulean');
        else if (opts.variant === 'new') el.classList.add('food-map__chip--new');
        el.dataset.axis = opts.axis;
        el.dataset.value = opts.value;
        if (opts.color) el.style.setProperty('--chip-color', opts.color);
        if (opts.onColor) el.style.setProperty('--chip-on-color', opts.onColor);
        el.setAttribute('aria-pressed', 'false');

        if (opts.variant === 'cuisine') {
            const dot = document.createElement('span');
            dot.className = 'food-map__chip-dot';
            dot.setAttribute('aria-hidden', 'true');
            el.appendChild(dot);
        }
        el.appendChild(document.createTextNode(opts.label));

        el.addEventListener('click', function () {
            toggleFilter(opts.axis, opts.value, el);
        });
        return el;
    }

    function toggleFilter(axis, value, chipEl) {
        if (axis === 'isNew') {
            filterState.isNew = !filterState.isNew;
            chipEl.classList.toggle('is-active', filterState.isNew);
            chipEl.setAttribute('aria-pressed', String(filterState.isNew));
        } else {
            const set = filterState[axis];
            if (set.has(value)) {
                set.delete(value);
                chipEl.classList.remove('is-active');
                chipEl.setAttribute('aria-pressed', 'false');
            } else {
                set.add(value);
                chipEl.classList.add('is-active');
                chipEl.setAttribute('aria-pressed', 'true');
            }
        }
        applyFilters();
        if (axis === 'cuisine' || axis === 'area') {
            const row = document.querySelector('[data-filter-row]');
            if (row) reorderAxisChips(row, axis);
        }
    }

    function applyFilters() {
        let visible = 0;
        pinRecords.forEach(function (rec) {
            const ok = matchesFilters(rec.pin);
            if (ok) visible++;
            rec.marker.getElement().style.display = ok ? '' : 'none';
        });
        updateCount(visible);
        updateFiltersBadge();
        urlState.setFilters(filterState);
        if (mapArea && mapArea.classList.contains('is-list-view')) renderList();
    }

    // URL-restore pass: renderFilters paints chips inactive; this flips on whatever the URL hydrated.
    function syncChipsToState() {
        const row = document.querySelector('[data-filter-row]');
        if (!row) return;
        const chips = row.querySelectorAll('.food-map__chip[data-value]');
        Array.prototype.forEach.call(chips, function (chip) {
            const axis = chip.dataset.axis;
            const value = chip.dataset.value;
            let active = false;
            if (axis === 'isNew') active = filterState.isNew;
            else if (filterState[axis]) active = filterState[axis].has(value);
            if (active) {
                chip.classList.add('is-active');
                chip.setAttribute('aria-pressed', 'true');
            }
        });
        ['cuisine', 'area'].forEach(function (axis) {
            reorderAxisChips(row, axis);
        });
    }

    // Reads the post-render chip labels so the SR hears the exact text users see.
    function announceActiveFilters() {
        const chips = document.querySelectorAll('.food-map__chip.is-active');
        if (!chips.length) return;
        const labels = Array.prototype.map.call(chips, function (c) {
            return c.textContent.trim();
        });
        announce('Filters applied from link: ' + labels.join(', ') + '.');
    }

    function matchesFilters(pin) {
        if (filterState.searchQuery) {
            const q = filterState.searchQuery;
            const haystack = [
                pin.name || '',
                pin.address || '',
                pin.cuisines
                    .map(function (c) {
                        return formatAxisLabel(c.name, c.slug, 'cuisine');
                    })
                    .join(' '),
            ]
                .join(' ')
                .toLowerCase();
            if (haystack.indexOf(q) === -1) return false;
        }
        if (filterState.cuisine.size) {
            const hit = pin.cuisines.some(function (c) {
                return filterState.cuisine.has(c.slug);
            });
            if (!hit) return false;
        }
        if (filterState.area.size && !filterState.area.has(pin.area)) return false;
        if (filterState.price.size && !filterState.price.has(pin.price)) return false;
        if (filterState.isNew && !pin.isNew) return false;
        return true;
    }

    function escapeHtml(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }
    function escapeAttr(s) {
        return escapeHtml(s);
    }
})();
